import { Controller } from "@hotwired/stimulus"

// Shows tooltip with missing required fields when hovering submit button
export default class extends Controller {
  static targets = ["button", "tooltip"]
  static values = {
    formSelector: { type: String, default: "form" }
  }

  connect() {
    this.updateTooltip()
  }

  show() {
    this.updateTooltip()
    if (this.missingFields.length > 0 && this.hasTooltipTarget) {
      this.tooltipTarget.classList.remove('hidden')
    }
  }

  hide() {
    if (this.hasTooltipTarget) {
      this.tooltipTarget.classList.add('hidden')
    }
  }

  updateTooltip() {
    this.missingFields = this.getMissingFields()

    if (this.hasTooltipTarget) {
      if (this.missingFields.length > 0) {
        this.tooltipTarget.innerHTML = this.buildTooltipContent()
      } else {
        this.tooltipTarget.innerHTML = ''
      }
    }
  }

  getMissingFields() {
    const form = this.element.closest(this.formSelectorValue) || document.querySelector(this.formSelectorValue)
    if (!form) return []

    const missing = []
    const requiredFields = form.querySelectorAll('[required]:not([disabled])')

    requiredFields.forEach(field => {
      // Skip hidden fields
      if (field.offsetParent === null) return

      // Check if field is empty
      let isEmpty = false
      if (field.type === 'checkbox' || field.type === 'radio') {
        // For radio buttons, check if any in the group is checked
        const name = field.name
        if (name) {
          const group = form.querySelectorAll(`input[name="${name}"]`)
          isEmpty = !Array.from(group).some(r => r.checked)
        } else {
          isEmpty = !field.checked
        }
      } else {
        isEmpty = !field.value || field.value.trim() === ''
      }

      if (isEmpty) {
        const label = this.getFieldLabel(field)
        if (label && !missing.includes(label)) {
          missing.push(label)
        }
      }
    })

    return missing
  }

  getFieldLabel(field) {
    // Um radio cujo <label> é o card inteiro (produto: nome, descrição, preço)
    // diz o nome do campo aqui, senão o aviso lista o card como campo faltando.
    if (field.dataset.fieldLabel) return field.dataset.fieldLabel

    // Try to find associated label
    if (field.id) {
      const label = document.querySelector(`label[for="${field.id}"]`)
      if (label) {
        // Get text content without any child element text (like "(optional)")
        const clone = label.cloneNode(true)
        // Remove optional spans and asterisks
        clone.querySelectorAll('span').forEach(s => s.remove())
        return clone.textContent.trim().replace(/\*$/, '').trim()
      }
    }

    // Try to find label by wrapping or proximity
    const wrapper = field.closest('div')
    if (wrapper) {
      const label = wrapper.querySelector('label')
      if (label) {
        const clone = label.cloneNode(true)
        clone.querySelectorAll('span').forEach(s => s.remove())
        return clone.textContent.trim().replace(/\*$/, '').trim()
      }
    }

    // Fall back to name or placeholder
    if (field.placeholder) return field.placeholder
    if (field.name) {
      return field.name
        .replace(/\[|\]/g, ' ')
        .replace(/_/g, ' ')
        .trim()
    }

    return 'Campo obrigatório'
  }

  buildTooltipContent() {
    const maxDisplay = 5
    const display = this.missingFields.slice(0, maxDisplay)
    const remaining = this.missingFields.length - maxDisplay

    let content = `
      <div class="text-sm">
        <div class="font-medium text-gray-900 mb-2 flex items-center gap-1.5">
          <svg class="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          Campos obrigatórios faltando:
        </div>
        <ul class="space-y-1 text-gray-600">
    `

    display.forEach(field => {
      content += `<li class="flex items-center gap-1.5">
        <span class="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
        ${field}
      </li>`
    })

    if (remaining > 0) {
      content += `<li class="text-gray-500 italic">+ ${remaining} outro(s) campo(s)</li>`
    }

    content += '</ul></div>'

    return content
  }
}
